import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { surveysService } from './surveysService';
import {
  Survey,
  Questionnaire,
  Checklist,
  SurveyResponse,
  SurveySafetyFlag,
  SurveysKpi,
  ChecklistExecution,
} from './types';
import { useDialog } from '../../shared/DialogContext';
import { TablePagination, useTablePagination } from '../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../shared/useTableSort';

export const SurveysPage: React.FC = () => {
  const { showError, showSuccess } = useDialog();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [flags, setFlags] = useState<SurveySafetyFlag[]>([]);
  const [executions, setExecutions] = useState<ChecklistExecution[]>([]);
  const [kpis, setKpis] = useState<SurveysKpi | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'kpi' | 'surveys' | 'questionnaires' | 'checklists' | 'responses' | 'flags'>('kpi');

  const { sortedItems: sortedSurveys, sortField: surveySortField, sortDirection: surveySortDir, toggleSort: toggleSurveySort } = useTableSort(surveys);
  const { sortedItems: sortedResponses, sortField: respSortField, sortDirection: respSortDir, toggleSort: toggleRespSort } = useTableSort(responses);
  const { sortedItems: sortedExecutions, sortField: execSortField, sortDirection: execSortDir, toggleSort: toggleExecSort } = useTableSort(executions);

  const {
    currentPage: surveyPage,
    setCurrentPage: setSurveyPage,
    pageSize: surveyPageSize,
    setPageSize: setSurveyPageSize,
    totalItems: totalSurveyItems,
    paginatedItems: paginatedSurveys,
  } = useTablePagination(sortedSurveys, 25);

  const {
    currentPage: respPage,
    setCurrentPage: setRespPage,
    pageSize: respPageSize,
    setPageSize: setRespPageSize,
    totalItems: totalRespItems,
    paginatedItems: paginatedResponses,
  } = useTablePagination(sortedResponses, 25);

  // Modal State
  const [showNewSurveyModal, setShowNewSurveyModal] = useState<boolean>(false);
  const [newSurveyTitle, setNewSurveyTitle] = useState<string>('');
  const [newSurveyCode, setNewSurveyCode] = useState<string>('');
  const [newSurveyQnrId, setNewSurveyQnrId] = useState<string>('');
  const [newSurveyAudience, setNewSurveyAudience] = useState<string>('ALL_EMPLOYEES');
  const [newSurveyAnon, setNewSurveyAnon] = useState<boolean>(true);

  // Response simulation modal state
  const [showSubmitModal, setShowSubmitModal] = useState<boolean>(false);
  const [targetSurveyId, setTargetSurveyId] = useState<string>('');
  const [respDept, setRespDept] = useState<string>('Flight Operations');
  const [respRating, setRespRating] = useState<string>('QOPT-01-5');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [srvList, qnrList, chkList, respList, flagList, kpiData, execList] = await Promise.all([
        surveysService.getSurveys(),
        surveysService.getQuestionnaires(),
        surveysService.getChecklists(),
        surveysService.getResponses(),
        surveysService.getSafetyFlags(),
        surveysService.getKpis(),
        surveysService.getChecklistExecutions(),
      ]);
      setSurveys(srvList);
      setQuestionnaires(qnrList);
      setChecklists(chkList);
      setResponses(respList);
      setFlags(flagList);
      setKpis(kpiData);
      setExecutions(execList);
    } catch (err) {
      console.error('Failed to load surveys data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSurvey = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await surveysService.createSurvey({
        code: newSurveyCode,
        title: newSurveyTitle,
        questionnaire_id: newSurveyQnrId,
        target_audience_type: newSurveyAudience,
        is_anonymous: newSurveyAnon,
        valid_from: new Date().toISOString(),
        valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      });
      setShowNewSurveyModal(false);
      setNewSurveyTitle('');
      setNewSurveyCode('');
      await loadData();
      showSuccess('Safety Survey campaign created successfully.');
    } catch (err: any) {
      showError(err, 'Failed to Create Survey');
    }
  };

  const handleSubmitQuickResponse = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await surveysService.submitResponse({
        survey_id: targetSurveyId,
        respondent_department: respDept,
        completion_time_seconds: 145,
        answers: [
          {
            question_id: 'Q-01',
            selected_option_id: respRating,
          },
        ],
      });
      setShowSubmitModal(false);
      await loadData();
      showSuccess('Survey response submitted successfully!');
    } catch (err: any) {
      showError(err, 'Failed to Submit Response');
    }
  };

  const handleEscalateFlag = async (flagId: string) => {
    try {
      await surveysService.escalateFlagToHazard(flagId);
      await loadData();
      showSuccess('Safety flag successfully escalated to Hazard Management register!');
    } catch (err: any) {
      showError(err, 'Escalation Failed');
    }
  };

  return (
    <div>
      {/* 1. Header Banner */}
      <div className="page-header-banner">
        <div className="badge">AUDITS & CHECKLISTS</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Safety Surveys & Operational Checklists</h1>
            <p>
              Campaign management, reusable question banks, Part-145 operational checklists, and anonymous risk flagging engine.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => {
                if (questionnaires.length > 0) setNewSurveyQnrId(questionnaires[0].id);
                setShowNewSurveyModal(true);
              }}
              className="btn-primary"
              style={{ width: 'auto', padding: '10px 18px' }}
            >
              + Launch Survey Campaign
            </button>
            <button
              onClick={() => {
                if (surveys.length > 0) setTargetSurveyId(surveys[0].id);
                setShowSubmitModal(true);
              }}
              className="btn-secondary"
              style={{ width: 'auto' }}
            >
              ✍️ Submit Response
            </button>
          </div>
        </div>
      </div>

      {/* 2. Navigation Tabs */}
      <div className="nav-tabs">
        <button
          onClick={() => setActiveTab('kpi')}
          className={`tab-btn ${activeTab === 'kpi' ? 'active' : ''}`}
        >
          📊 Dashboard & KPI
        </button>
        <button
          onClick={() => setActiveTab('surveys')}
          className={`tab-btn ${activeTab === 'surveys' ? 'active' : ''}`}
        >
          🗳️ Active Campaigns ({surveys.length})
        </button>
        <button
          onClick={() => setActiveTab('questionnaires')}
          className={`tab-btn ${activeTab === 'questionnaires' ? 'active' : ''}`}
        >
          📑 Questionnaires ({questionnaires.length})
        </button>
        <button
          onClick={() => setActiveTab('checklists')}
          className={`tab-btn ${activeTab === 'checklists' ? 'active' : ''}`}
        >
          ✅ Audit Checklists ({checklists.length})
        </button>
        <button
          onClick={() => setActiveTab('responses')}
          className={`tab-btn ${activeTab === 'responses' ? 'active' : ''}`}
        >
          📥 Submissions ({responses.length})
        </button>
        <button
          onClick={() => setActiveTab('flags')}
          className={`tab-btn ${activeTab === 'flags' ? 'active' : ''}`}
        >
          🚩 Safety Flags ({flags.filter((f) => f.status === 'OPEN').length})
        </button>
      </div>

      {/* 3. TAB CONTENT */}

      {/* TAB 1: KPI Dashboard */}
      {activeTab === 'kpi' && kpis && (
        <div>
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-label">Active Campaigns</span>
              <span className="stat-value" style={{ color: '#2563eb' }}>{kpis.active_surveys}</span>
              <span className="stat-sub">Scheduled: {kpis.scheduled_surveys}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Total Submissions</span>
              <span className="stat-value">{kpis.total_responses}</span>
              <span className="stat-sub">Response Rate: {kpis.average_response_rate_percent}%</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Average Safety Score</span>
              <span className="stat-value" style={{ color: '#0d9488' }}>{kpis.average_score} / 100</span>
              <span className="stat-sub">Weighted Likert Metrics</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Open Safety Alarms</span>
              <span className="stat-value" style={{ color: '#dc2626' }}>{kpis.open_safety_flags}</span>
              <span className="stat-sub">Critical / Just Culture Flags</span>
            </div>
          </div>

          <div className="card" style={{ background: '#f8fafc', borderLeft: '4px solid #3b82f6' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>
              🛡️ Survey & Inspection Architectural Principles
            </h3>
            <ul style={{ fontSize: '13px', color: '#475569', lineHeight: 1.6, paddingLeft: '20px' }}>
              <li><strong>Survey ≠ Questionnaire:</strong> Surveys are campaign instances with deadlines; Questionnaires are reusable version-controlled template items.</li>
              <li><strong>Checklist ≠ Survey:</strong> Checklists drive operational pass/fail verifications and instantly dispatch CAPA actions upon negative outcomes.</li>
              <li><strong>Unified Response Store:</strong> Both surveys and audit checklists log through the shared answer evaluation engine.</li>
              <li><strong>Anonymity Guarantee:</strong> Anonymous surveys never persist user credentials or traceable device signatures.</li>
            </ul>
          </div>
        </div>
      )}

      {/* TAB 2: Surveys */}
      {activeTab === 'surveys' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh field="code" currentField={surveySortField} currentDirection={surveySortDir} onSort={toggleSurveySort}>
                  Code
                </SortableTh>
                <SortableTh field="title" currentField={surveySortField} currentDirection={surveySortDir} onSort={toggleSurveySort}>
                  Campaign Title
                </SortableTh>
                <SortableTh field="target_audience_type" currentField={surveySortField} currentDirection={surveySortDir} onSort={toggleSurveySort}>
                  Audience
                </SortableTh>
                <SortableTh field="is_anonymous" currentField={surveySortField} currentDirection={surveySortDir} onSort={toggleSurveySort}>
                  Confidentiality
                </SortableTh>
                <SortableTh field="status" currentField={surveySortField} currentDirection={surveySortDir} onSort={toggleSurveySort}>
                  Status
                </SortableTh>
                <SortableTh field="participant_count" currentField={surveySortField} currentDirection={surveySortDir} onSort={toggleSurveySort}>
                  Participation
                </SortableTh>
                <th style={{ textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSurveys.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1e293b' }}>{s.code}</td>
                  <td>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{s.title}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>{s.questionnaire_title || 'Default Form'}</div>
                  </td>
                  <td style={{ fontSize: '12px', color: '#475569' }}>{s.target_audience_type}</td>
                  <td>
                    <span className={`badge-pill ${s.is_anonymous ? 'badge-warning' : 'badge-info'}`}>
                      {s.is_anonymous ? '🔒 Anonymous' : '👤 Identified'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge-pill ${s.status === 'OPEN' ? 'badge-success' : 'badge-neutral'}`}>
                      {s.status}
                    </span>
                  </td>
                  <td style={{ fontSize: '12px', color: '#475569' }}>
                    {s.total_responses} responses ({s.response_rate_percent}%)
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={() => {
                        setTargetSurveyId(s.id);
                        setShowSubmitModal(true);
                      }}
                      className="btn-secondary"
                      style={{ padding: '4px 12px', fontSize: '12px' }}
                    >
                      Fill Survey
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination
            currentPage={surveyPage}
            totalItems={totalSurveyItems}
            pageSize={surveyPageSize}
            onPageChange={setSurveyPage}
            onPageSizeChange={setSurveyPageSize}
            itemLabel="surveys"
          />
        </div>
      )}

      {/* TAB 3: Questionnaires */}
      {activeTab === 'questionnaires' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
          {questionnaires.map((q) => (
            <div key={q.id} className="card" style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, color: '#64748b' }}>
                  {q.code} (v{q.version_no})
                </span>
                <span className={`badge-pill ${q.status === 'PUBLISHED' ? 'badge-success' : 'badge-warning'}`}>
                  {q.status}
                </span>
              </div>
              <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>
                {q.title}
              </h3>
              <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>
                {q.description || 'No description specified.'}
              </p>
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '10px' }}>
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#475569', textTransform: 'uppercase' }}>
                  Sections ({q.sections?.length || 0}):
                </span>
                <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {q.sections?.map((sec) => (
                    <div
                      key={sec.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '12px',
                        color: '#475569',
                        padding: '4px 0',
                        borderBottom: '1px solid #f1f5f9',
                      }}
                    >
                      <span>• {sec.title}</span>
                      <span style={{ color: '#94a3b8' }}>Weight: {sec.weight_percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TAB 4: Checklists */}
      {activeTab === 'checklists' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            {checklists.map((chk) => (
              <div key={chk.id} className="card" style={{ marginBottom: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, color: '#64748b' }}>
                    {chk.code}
                  </span>
                  <span className="badge-pill badge-info">{chk.category}</span>
                </div>
                <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>
                  {chk.title}
                </h3>
                <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>{chk.description}</p>
                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '10px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: '#475569', textTransform: 'uppercase' }}>
                    Items ({chk.items?.length || 0}):
                  </span>
                  <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {chk.items?.map((itm) => (
                      <div
                        key={itm.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '12px',
                          color: '#475569',
                          padding: '4px 0',
                          borderBottom: '1px solid #f1f5f9',
                        }}
                      >
                        <span>{itm.sequence_no}. {itm.title}</span>
                        {itm.is_critical_stop_work && (
                          <span className="badge-pill badge-danger" style={{ fontSize: '10px' }}>
                            STOP-WORK
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Executions Table */}
          <div className="card">
            <h4 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>
              Operational Checklist Verification Log
            </h4>
            <div className="table-container" style={{ marginBottom: 0 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <SortableTh field="title" currentField={execSortField} currentDirection={execSortDir} onSort={toggleExecSort}>
                      Verification Title
                    </SortableTh>
                    <SortableTh field="executor_user_id" currentField={execSortField} currentDirection={execSortDir} onSort={toggleExecSort}>
                      Auditor
                    </SortableTh>
                    <SortableTh field="compliance_score" currentField={execSortField} currentDirection={execSortDir} onSort={toggleExecSort}>
                      Compliance Score
                    </SortableTh>
                    <SortableTh field="status" currentField={execSortField} currentDirection={execSortDir} onSort={toggleExecSort}>
                      Status
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {sortedExecutions.map((e) => (
                    <tr key={e.id}>
                      <td style={{ fontWeight: 600, color: '#0f172a' }}>{e.title}</td>
                      <td style={{ color: '#475569' }}>{e.executor_user_id}</td>
                      <td style={{ fontWeight: 700, color: '#059669' }}>{e.compliance_score}%</td>
                      <td>
                        <span className={`badge-pill ${e.status === 'COMPLETED' ? 'badge-success' : 'badge-danger'}`}>
                          {e.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 5: Responses */}
      {activeTab === 'responses' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh field="id" currentField={respSortField} currentDirection={respSortDir} onSort={toggleRespSort}>
                  Response ID
                </SortableTh>
                <SortableTh field="survey_title" currentField={respSortField} currentDirection={respSortDir} onSort={toggleRespSort}>
                  Survey Campaign
                </SortableTh>
                <SortableTh field="respondent_department" currentField={respSortField} currentDirection={respSortDir} onSort={toggleRespSort}>
                  Department
                </SortableTh>
                <SortableTh field="calculated_score" currentField={respSortField} currentDirection={respSortDir} onSort={toggleRespSort}>
                  Score
                </SortableTh>
                <SortableTh field="is_anonymous" currentField={respSortField} currentDirection={respSortDir} onSort={toggleRespSort}>
                  Confidentiality
                </SortableTh>
                <SortableTh field="completion_seconds" currentField={respSortField} currentDirection={respSortDir} onSort={toggleRespSort}>
                  Duration
                </SortableTh>
                <SortableTh field="submitted_at" currentField={respSortField} currentDirection={respSortDir} onSort={toggleRespSort}>
                  Submitted Date
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {paginatedResponses.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1e293b' }}>{r.id}</td>
                  <td style={{ fontWeight: 600, color: '#0f172a' }}>{r.survey_title || r.survey_id}</td>
                  <td style={{ fontSize: '12px', color: '#475569' }}>{r.respondent_department}</td>
                  <td style={{ fontWeight: 700, color: '#2563eb' }}>{r.overall_score}%</td>
                  <td>
                    {r.respondent_user_id ? (
                      <span style={{ fontSize: '12px', color: '#475569' }}>{r.respondent_user_id}</span>
                    ) : (
                      <span className="badge-pill badge-warning">
                        🔒 Anonymous
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: '12px', color: '#64748b' }}>{r.completion_time_seconds}s</td>
                  <td style={{ fontSize: '12px', color: '#64748b' }}>{r.submitted_at?.substring(0, 16)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination
            currentPage={respPage}
            totalItems={totalRespItems}
            pageSize={respPageSize}
            onPageChange={setRespPage}
            onPageSizeChange={setRespPageSize}
            itemLabel="responses"
          />
        </div>
      )}

      {/* TAB 6: Safety Flags */}
      {activeTab === 'flags' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {flags.map((f) => (
            <div
              key={f.id}
              className="card"
              style={{
                marginBottom: 0,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
                borderLeft: '4px solid #ef4444',
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span className="badge-pill badge-danger">{f.severity}</span>
                  <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '14px' }}>{f.flag_type}</span>
                  <span className={`badge-pill ${f.status === 'OPEN' ? 'badge-warning' : 'badge-success'}`}>
                    {f.status}
                  </span>
                </div>
                <p style={{ fontSize: '13px', color: '#475569', margin: '4px 0' }}>{f.description}</p>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>Response Ref: {f.response_id}</span>
              </div>
              {f.status === 'OPEN' && (
                <button
                  onClick={() => handleEscalateFlag(f.id)}
                  className="btn-primary"
                  style={{ width: 'auto', background: '#dc2626', padding: '8px 16px', fontSize: '12px' }}
                >
                  ⚠️ Escalate to Hazard Register
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal: New Survey */}
      {showNewSurveyModal && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <div className="modal-header">
              <div className="modal-header-content">
                <h3>Create Safety Survey Campaign</h3>
                <p>Launch targeted safety culture or just-culture surveys</p>
              </div>
              <button
                type="button"
                onClick={() => setShowNewSurveyModal(false)}
                className="modal-close-btn"
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateSurvey}>
              <div className="modal-body login-form">
                <div className="form-field">
                  <label>Campaign Code</label>
                  <input
                    type="text"
                    required
                    placeholder="SRV-2026-Q3-SAFETY"
                    value={newSurveyCode}
                    onChange={(e) => setNewSurveyCode(e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label>Campaign Title</label>
                  <input
                    type="text"
                    required
                    placeholder="2026 Q3 Cockpit Just Culture Assessment"
                    value={newSurveyTitle}
                    onChange={(e) => setNewSurveyTitle(e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label>Questionnaire Bank Template</label>
                  <select
                    value={newSurveyQnrId}
                    onChange={(e) => setNewSurveyQnrId(e.target.value)}
                  >
                    {questionnaires.map((q) => (
                      <option key={q.id} value={q.id}>{q.title} ({q.code})</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Target Audience</label>
                  <select
                    value={newSurveyAudience}
                    onChange={(e) => setNewSurveyAudience(e.target.value)}
                  >
                    <option value="ALL_EMPLOYEES">All Enterprise Personnel</option>
                    <option value="PILOTS">Flight Deck Crew (Pilots)</option>
                    <option value="CABIN_CREW">Cabin Services</option>
                    <option value="MAINTENANCE_TECH">Part-145 Maintenance Engineers</option>
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '8px' }}>
                  <input
                    type="checkbox"
                    id="anonCheck"
                    checked={newSurveyAnon}
                    onChange={(e) => setNewSurveyAnon(e.target.checked)}
                  />
                  <label htmlFor="anonCheck" style={{ fontSize: '13px', color: '#334155', cursor: 'pointer' }}>
                    Enforce Complete Anonymity (Zero identity tokens stored)
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowNewSurveyModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ width: 'auto' }}
                >
                  Launch Campaign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Quick Response Simulation */}
      {showSubmitModal && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <div className="modal-header">
              <div className="modal-header-content">
                <h3>Submit Survey Response</h3>
                <p>Simulate survey input with safety flag threshold trigger testing</p>
              </div>
              <button
                type="button"
                onClick={() => setShowSubmitModal(false)}
                className="modal-close-btn"
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmitQuickResponse}>
              <div className="modal-body login-form">
                <div className="form-field">
                  <label>Target Campaign</label>
                  <select
                    value={targetSurveyId}
                    onChange={(e) => setTargetSurveyId(e.target.value)}
                  >
                    {surveys.map((s) => (
                      <option key={s.id} value={s.id}>{s.title} ({s.code})</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Participant Department</label>
                  <select
                    value={respDept}
                    onChange={(e) => setRespDept(e.target.value)}
                  >
                    <option value="Flight Operations">Flight Operations (Cockpit)</option>
                    <option value="Cabin Crew">Cabin Crew Services</option>
                    <option value="Maintenance & Engineering">Maintenance & Engineering</option>
                    <option value="Ground Operations">Ground Handling & Ramp</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Question: Do you feel safety reports are handled fairly without penalty?</label>
                  <select
                    value={respRating}
                    onChange={(e) => setRespRating(e.target.value)}
                  >
                    <option value="QOPT-01-1">1 - Strongly Disagree (Triggers Safety Flag!)</option>
                    <option value="QOPT-01-2">2 - Disagree (Triggers Safety Flag!)</option>
                    <option value="QOPT-01-3">3 - Neutral</option>
                    <option value="QOPT-01-4">4 - Agree</option>
                    <option value="QOPT-01-5">5 - Strongly Agree</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowSubmitModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ width: 'auto', background: '#10b981' }}
                >
                  Submit Response
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default SurveysPage;
